import { OSYNC_FILTER_FIELDS, CHANNEL_FILTER_TYPE } from "./filterFieldConfig";

const isNestedFilterGroup = (condition) =>
    Array.isArray(condition?.conditions) && condition.conditions.length > 0;

/**
 * When both integration channel UUIDs (rightServiceId) and phone-backed channels (orgMember)
 * are present, combine them with OR. AND would require the same conversation to match both,
 * which fails for cross-provider selections (e.g. Twilio UUID + RingCentral line).
 *
 * @param {Array<Object>} conditions - flat leaf conditions from {@link buildFilterDefinitionFromState}
 * @returns {Array<Object>}
 */
const mergeRightServiceIdAndOrgMemberWithOr = (conditions) => {
    if (!Array.isArray(conditions) || conditions.length < 2) {
        return conditions;
    }
    let indexRightServiceId = -1;
    let indexOrgMember = -1;
    for (let i = 0; i < conditions.length; i++) {
        const c = conditions[i];
        if (isNestedFilterGroup(c)) {
            continue;
        }
        if (c?.field === "rightServiceId" && indexRightServiceId < 0) {
            indexRightServiceId = i;
        } else if (c?.field === "orgMember" && indexOrgMember < 0) {
            indexOrgMember = i;
        }
    }
    if (indexRightServiceId < 0 || indexOrgMember < 0) {
        return conditions;
    }
    const rightLeaf = conditions[indexRightServiceId];
    const orgLeaf = conditions[indexOrgMember];
    const rest = conditions.filter((_, i) => i !== indexRightServiceId && i !== indexOrgMember);
    const insertAt = Math.min(indexRightServiceId, indexOrgMember);
    const orGroup = {
        groupOperator: "OR",
        conditions: [
            {
                field: "rightServiceId",
                operator: rightLeaf.operator,
                value: rightLeaf.value,
            },
            { field: "orgMember", operator: orgLeaf.operator, value: orgLeaf.value },
        ],
    };
    rest.splice(insertAt, 0, orGroup);
    return rest;
};

/**
 * Normalized filter state used by both "apply filter" (inbox fetch) and "save filter" (saved views).
 * Keys must match OSYNC_FILTER_FIELDS[].stateKey in filterFieldConfig.js.
 *
 * @typedef {Object} InboxFilterState
 * @property {string[]} [serviceIds] - Channel (rightServiceId) UUIDs
 * @property {string[]} [orgMembers] - Phone/org member values for orgMember filter
 * @property {string[]} [associatedTeamIds] - Team IDs for associatedTeams (DynamoDB Set → OR of CONTAINS)
 * @property {string[]} [contactOwnerEntityIds] - Assignee IDs (contactOwnerEntityId IN)
 * @property {string|null} [unreadUserId] - If set, add userUnreadCountMap IN [userId]
 * @property {string[]} [conversationStatusFilter]
 * @property {string[]} [userFlagColorMap]
 * @property {string|null} [startDateTime] - ISO string
 * @property {string|null} [endDateTime] - ISO string
 * @property {string} [dateField] - 'messageTime' (default) or 'createdTime'
 */

/**
 * Builds filterDefinition from normalized state using OSYNC_FILTER_FIELDS (field names and operators from config).
 *
 * @param {InboxFilterState} state
 * @returns {{ groupOperator: string, conditions: Array }}
 */
export const buildFilterDefinitionFromState = (state) => {
    if (!state) return { groupOperator: "AND", conditions: [] };
    const conditions = [];

    for (const field of OSYNC_FILTER_FIELDS) {
        if (!field.stateKey) continue;

        const rawValue = field.endStateKey
            ? state[field.stateKey] && state[field.endStateKey]
                ? [state[field.stateKey], state[field.endStateKey]]
                : null
            : state[field.stateKey];

        const value = field.stateValueTransform ? field.stateValueTransform(rawValue) : rawValue;
        if (value == null || (Array.isArray(value) && value.length === 0)) continue;

        const apiField = field.apiField ?? field.fieldName;
        const operator = field.api.operator;

        if (field.endStateKey && Array.isArray(value) && value.length >= 2) {
            const resolvedField = field.dateFieldOverride && state.dateField ? state.dateField : apiField;
            conditions.push({ field: resolvedField, operator, value: [value[0], value[1]] });
        } else {
            conditions.push({
                field: apiField,
                operator,
                value: Array.isArray(value) ? value : [value],
            });
        }
    }

    return {
        groupOperator: "AND",
        conditions: mergeRightServiceIdAndOrgMemberWithOr(conditions),
    };
};

/** Returns null if state produced no conditions (e.g. for inbox fetch when no filters applied). */
export const buildFilterDefinitionFromStateOrNull = (state) => {
    const fd = buildFilterDefinitionFromState(state);
    return fd && fd.conditions && fd.conditions.length > 0 ? fd : null;
};

/**
 * Returns options for a field from its config. Uses field.optionsKey to look up in context,
 * or field.staticOptions() for fields with fixed option lists.
 */
export const getOptionsForField = (field, context = {}) => {
    if (field.staticOptions) return field.staticOptions();
    if (field.optionsKey) return context[field.optionsKey] ?? null;
    return null;
};

/**
 * Maps temp filter form values + options to API filterDefinition using OSYNC_FILTER_FIELDS transforms.
 *
 * @param {Object} tempFilterValues - Current filter values keyed by fieldName (from InboxFilterPopoverCard state)
 * @param {Object} [options] - Optional. e.g. { channelFilterType: "channel" | "team" }
 * @returns {{ groupOperator: string, conditions: Array } | null}
 */
export const buildFilterDefinitionForSave = (tempFilterValues, options = {}) => {
    const { channelFilterType } = options;
    const conditions = [];

    for (const field of OSYNC_FILTER_FIELDS) {
        if (!field.visible) continue;

        const value = tempFilterValues?.[field.fieldName];
        if (value == null || (Array.isArray(value) && value.length === 0)) continue;

        if (field.fieldName === "rightServiceId" && channelFilterType === CHANNEL_FILTER_TYPE.TEAM) {
            conditions.push({ field: "associatedTeams", operator: "CONTAINS", value });
            continue;
        }

        if (field.api.transform) {
            const fieldConditions = field.api.transform(value);
            conditions.push(...fieldConditions);
        } else {
            conditions.push({
                field: field.apiField ?? field.fieldName,
                operator: field.api.operator,
                value: Array.isArray(value) ? value : [value],
            });
        }
    }

    return conditions.length > 0 ? { groupOperator: "AND", conditions } : null;
};
